import { createSlice } from '@reduxjs/toolkit';
import { PatientDocumentAssociationType } from '../../../enums/PatientDocumentAssociationType';
import { commonService } from '../../../services/commonServiceInstance';
import { handleApiCall } from '../../../helpers/handleApiCall';
import { clearComponentError } from '../../../components/ComponentError/redux';

// NOTE: because this component can be used more than once one
// the same page, each "instanceName" in this redux slice will have this
// initial state when init() is called.
const initialState = {
    chartAssociationType: null,
    isChartLoaded: false,
    chartMetadata: null,
    page: null,
    chartText: null,
    loadingChartId: null,
};

const slice = createSlice({
    name: 'chartViewer',
    initialState: {},
    reducers: {
        init(state, action) {
            const instanceName = action.payload;
            if (state[instanceName]) return;
            state[instanceName] = { ...initialState };
        },
        reset(state, action) {
            const instanceName = action.payload;
            state[instanceName] = { ...initialState };
        },
        setIsChartLoaded(state, action) {
            const { instanceName, payload } = action.payload;
            state[instanceName].isChartLoaded = payload;
        },
        setChartMetadata(state, action) {
            const { instanceName, payload } = action.payload;
            state[instanceName].chartMetadata = payload;
        },
        setPage(state, action) {
            const { instanceName, payload } = action.payload;
            state[instanceName].page = payload;
        },
        setChartText(state, action) {
            const { instanceName, payload } = action.payload;
            state[instanceName].chartText = payload;
        },
        startChartLoad(state, action) {
            const { instanceName, chartId } = action.payload;
            state[instanceName].loadingChartId = chartId;
            state[instanceName].isChartLoaded = false;
            state[instanceName].chartMetadata = null;
        },
        completeChartMetadataLoad(state, action) {
            const { instanceName, chartId, metadata } = action.payload;
            if (state[instanceName].loadingChartId !== chartId) return;
            state[instanceName].chartText = null;
            state[instanceName].page = 1;
            state[instanceName].chartMetadata = metadata;
            state[instanceName].isChartLoaded = { chartId };
        },
    },
});

export const {
    init,
    reset,
    setIsChartLoaded,
    setChartMetadata,
    setPage,
    setChartText,
    startChartLoad,
    completeChartMetadataLoad,
} = slice.actions;
export default { reducer: slice.reducer, initialState: {} };

// Thunks (side effects)

export const loadChart =
    ({ chartId, chartAssociationType, componentId, instanceName }) =>
    async (dispatch) => {
        await dispatch(init(instanceName));
        await dispatch(clearComponentError({ componentId }));

        if (chartAssociationType === PatientDocumentAssociationType.Document) {
            await dispatch(startChartLoad({ instanceName, chartId }));
            return await dispatch(loadChartMetadata(chartId, componentId, instanceName));
        } else {
            // if is demographics or appointments, a virtual chart which doesn't need metadata
            return await dispatch(loadVirtualChart(chartId, instanceName));
        }
    };

const loadChartMetadata = (chartId, componentId, instanceName) => async (dispatch, getState) => {
    if (!chartId) return;

    let response;
    let requestError;
    try {
        // Defer shared UI error handling until we know this request still owns the viewer.
        // eslint-disable-next-line cc/require-handle-api-call
        response = await commonService.getChartRenderMetadata(chartId, instanceName, componentId);
    } catch (error) {
        requestError = error;
    }

    const isCurrentChart = () => getState().chartViewer[instanceName]?.loadingChartId === chartId;
    if (!isCurrentChart()) return;

    const result = await handleApiCall(
        async () => {
            if (!isCurrentChart()) {
                const error = new Error('The chart metadata request was superseded.');
                error.name = 'AbortError';
                throw error;
            }
            if (requestError) throw requestError;
            if (!response) throw new Error('The chart render metadata request returned nothing.');

            // A successful response with no resolved type means the client has no
            // matching PatientDocumentPreference configuration -- a configuration state,
            // not a load failure. Let it through so ChartSwitcher can render its
            // "no compatible document type" callout instead of a retryable error.
            // An unsuccessful response falls through to handleApiCall, which raises a
            // ValidationError carrying the API's own reason.
            const selectedFormat = response.supportedFormats?.[0];
            if (selectedFormat?.type === 'Pdf' && !selectedFormat.url)
                throw new Error('The chart render metadata is missing a PDF render URL.');
            return response;
        },
        { componentId, dispatch, errorMessage: 'Unable to load the document.' },
    );
    if (!result.wasSuccessful || !isCurrentChart()) return;

    const { pageCount, supportedFormats } = result;
    const stateMetadata = {
        pageCount,
        supportedFormats: supportedFormats.map(({ type }) => ({ type })),
    };

    await dispatch(completeChartMetadataLoad({ instanceName, chartId, metadata: stateMetadata }));

    return { pageCount, supportedFormats };
};

const loadVirtualChart = (chartId, instanceName) => async (dispatch) => {
    await dispatch(setChartText({ instanceName, payload: null }));
    await dispatch(setPage({ instanceName, payload: 1 }));
    await dispatch(setIsChartLoaded({ instanceName, payload: { chartId } }));
};

export const loadChartText =
    ({
        chartAssociationType,
        componentId,
        instanceName,
        id,
        returnRawData,
        isMomsAiScore = false,
    }) =>
    async (dispatch) => {
        if (!id) return;

        const result = await handleApiCall(
            async () => {
                if (chartAssociationType === PatientDocumentAssociationType.Document)
                    return await commonService.getDocumentText({
                        patientDocumentId: id,
                        returnRawData,
                    });
                if (chartAssociationType === PatientDocumentAssociationType.Appointment)
                    return await commonService.getAppointmentText({
                        appointmentId: id,
                    });
                if (chartAssociationType === PatientDocumentAssociationType.Demographic)
                    return await commonService.getDemographicsText({ patientId: id });
                if (chartAssociationType === PatientDocumentAssociationType.SummaryOfCare)
                    return await commonService.getSummaryOfCareText({ patientId: id });
                if (chartAssociationType === PatientDocumentAssociationType.Referral)
                    return await commonService.getReferralText({ referralId: id });
                if (chartAssociationType === PatientDocumentAssociationType.Moms)
                    return await commonService.getMomsText({
                        patientId: id,
                        isMomsAiScore,
                    });

                throw new Error(`Unsupported chartAssociationType: ${chartAssociationType}`);
            },
            { componentId, dispatch, errorMessage: 'Unable to load the document.' },
        );

        if (!result.wasSuccessful) return;

        await dispatch(setChartText({ instanceName, payload: result }));
    };

export const loadPdfBlob =
    ({ resourceUrl, componentId, signal }) =>
    async (dispatch) => {
        return await handleApiCall(
            async () => await commonService.loadChartResourceAsBlobUrl(resourceUrl, signal),
            {
                componentId,
                dispatch,
                errorMessage: 'Unable to load the PDF document.',
            },
        );
    };
